// src/pages/sow-draft/OpportunityStep.jsx
//
// Step 1 of the Draft SOW wizard: search + pick an opportunity to draft
// a SOW for, or resume an existing draft. Split out of SowDraftPage.jsx.
//
// Rows render through the shared DataTable (see FindingsTable.jsx for the
// same pattern) instead of one full-width card per opportunity -- the
// card layout wasted most of its width on 6 short fields per row and got
// worse the more opportunities there were.

import { useMemo } from "react";
import PropTypes from "prop-types";
import { FolderOpen } from "lucide-react";
import { Button, DataTable, Input, SurfaceCard } from "../../shared/ui";
import { formatCurrency, formatDate } from "./sowDraftHelpers";

export default function OpportunityStep({
  opps,
  filteredOpps,
  loading,
  search,
  onSearchChange,
  draftsMap,
  onSelect,
  onLoadExisting,
}) {
  const columns = useMemo(
    () => [
      {
        accessorKey: "opportunity_name",
        header: "Opportunity",
        cell: ({ row }) => {
          const o = row.original;
          const hasDraft = !!draftsMap[o.id];
          return (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-ink">{o.opportunity_name}</span>
              {hasDraft && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  ✓ Draft
                </span>
              )}
              {o.sf_opportunity_id && (
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-50 text-brand-600"
                  title="Has Salesforce ID"
                >
                  SF
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "account_name",
        header: "Account",
        cell: ({ getValue }) => getValue() || "—",
      },
      {
        accessorKey: "contract_type",
        header: "Contract",
        width: 120,
        cell: ({ getValue }) => (
          <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100">
            {getValue() || "—"}
          </span>
        ),
      },
      {
        accessorKey: "opportunity_amount",
        header: "Amount",
        width: 120,
        cell: ({ getValue }) => <span className="font-medium">{formatCurrency(getValue())}</span>,
      },
      { accessorKey: "stage", header: "Stage", width: 110 },
      {
        accessorKey: "project_start_date",
        header: "Start",
        width: 110,
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        accessorKey: "sf_opportunity_id",
        header: "SF ID",
        width: 130,
        cell: ({ getValue }) => (
          <span className="truncate" title={getValue() || ""}>
            {getValue()?.substring(0, 15) || "—"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        width: 160,
        align: "right",
        sortAllowed: false,
        cell: ({ row }) => {
          const o = row.original;
          const hasDraft = !!draftsMap[o.id];
          return (
            <div className="flex gap-2 justify-end">
              <Button size="sm" onClick={() => onSelect(o)}>
                New Draft
              </Button>
              {hasDraft && (
                <Button size="sm" variant="secondary" onClick={() => onLoadExisting(o)}>
                  <FolderOpen size={14} /> Load Draft
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [draftsMap, onSelect, onLoadExisting]
  );

  return (
    <SurfaceCard>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <h2 className="text-base font-semibold text-ink">Select Opportunity</h2>
        <div className="w-72">
          <Input
            type="search"
            placeholder="Search opportunities…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      <p className="text-xs text-muted mb-3">
        Showing {filteredOpps.length} of {opps.length}
      </p>

      <DataTable
        table={{ columns, rows: filteredOpps }}
        loading={loading}
        emptyMessage="No opportunities found. Try a different search term."
        entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50] }}
      />
    </SurfaceCard>
  );
}

OpportunityStep.propTypes = {
  opps: PropTypes.array.isRequired,
  filteredOpps: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  draftsMap: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  onLoadExisting: PropTypes.func.isRequired,
};
